import React from "react";
import MyStaffingPreferencesScreen from "./MyStaffingPreferencesScreen";
import { API } from "../../API";
import {
  CaregiverStaffingPreferenceTypes,
  CaregiverStaffingPreferencesResponse,
  StaffingPreference,
} from "../../messages";
import LoadingScaffold from "../LoadingScaffold";
import assertNever from "assert-never";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ErrorScaffold from "../ErrorScaffold";
import { translate } from "../../translate";
import { STRINGS } from "../../strings";
import { match } from "ts-pattern";
import { AppCaregiverState } from "../../system/AppState/AppCaregiverState";

interface StaffingPreferencesActivityProps {
  caregiver: AppCaregiverState;
  api: API;
  onClickGoBack: () => void;
}

const StaffingPreferencesActivity = (props: StaffingPreferencesActivityProps) => {
  const queryClient = useQueryClient();

  const sectionsQuery = useQuery({
    queryKey: ["caregivers-staffing-preferences", props.caregiver.caregiver.id],
    queryFn: () => props.api.getCaregiverStaffingPreferences(),
  });

  const mutation = useMutation({
    mutationFn: (req: {
      prefKey: CaregiverStaffingPreferenceTypes;
      answer: boolean | string | number | string[] | null;
    }) =>
      props.api.updateCaregiverStaffingPreferences({
        changes: { [mapStaffingPreferenceTypeToUpdateRequestType(req.prefKey)]: req.answer },
      }),
    onMutate: ({ prefKey, answer }) => {
      const prevSections =
        queryClient.getQueryData<CaregiverStaffingPreferencesResponse>([
          "caregivers-staffing-preferences",
          props.caregiver.caregiver,
        ])?.sections ?? [];

      const section = prevSections.find((section) =>
        section.preferences.find((pref) => pref.type === prefKey)
      );

      if (section !== undefined) {
        const prevPreference = section.preferences.find((pref) => pref.type === prefKey);

        if (prevPreference !== undefined) {
          const newPreference: StaffingPreference = match(prevPreference)
            .with({ questionType: "MultiSelect" }, (pref) => {
              return {
                ...pref,
                answer: answer as string[],
              };
            })
            .with({ questionType: "Select" }, (pref) => {
              return {
                ...pref,
                answer: answer as string,
              };
            })
            .with({ questionType: "Text" }, (pref) => {
              return {
                ...pref,
                answer: answer as string,
              };
            })
            .with({ questionType: "Switch" }, (pref) => {
              return {
                ...pref,
                answer: answer as boolean,
              };
            })
            .exhaustive();

          const newPreferencesArray: StaffingPreference[] = section.preferences.filter(
            (pref) => pref.type !== prefKey
          );
          newPreferencesArray.push(newPreference);

          const newSection = { ...section, preferences: newPreferencesArray };
          const newSectionsArray = prevSections.filter(
            (section) => section.title !== section.title
          );

          newSectionsArray.push(newSection);

          queryClient.setQueryData<CaregiverStaffingPreferencesResponse>(
            ["caregivers-staffing-preferences", props.caregiver.caregiver.id],
            { sections: newSectionsArray }
          );
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries([
        "caregivers-staffing-preferences",
        props.caregiver.caregiver.id,
      ]);
    },
    onError: () => {
      const prevQueryData = queryClient.getQueryData<CaregiverStaffingPreferencesResponse>([
        "caregivers-staffing-preferences",
        props.caregiver.caregiver.id,
      ]);
      queryClient.setQueryData<CaregiverStaffingPreferencesResponse>(
        ["caregivers-staffing-preferences", props.caregiver.caregiver.id],
        prevQueryData
      );
    },
  });

  const handleUpdateCaregiverStaffingPreferences = (
    preferenceTypeToUpdate: CaregiverStaffingPreferenceTypes,
    answer: boolean | string | number | string[] | null
  ) => {
    mutation.mutate({ prefKey: preferenceTypeToUpdate, answer: answer });
  };

  return match(sectionsQuery.status)
    .with("error", () => {
      return (
        <ErrorScaffold
          title={translate(STRINGS.MY_STAFFING_PREFERENCES)}
          message={translate(STRINGS.SOMETHING_WENT_WRONG)}
          onClickRetry={sectionsQuery.refetch}
          onClickGoBack={props.onClickGoBack}
        />
      );
    })
    .with("loading", () => {
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    })
    .with("success", () => {
      return (
        <MyStaffingPreferencesScreen
          sections={sectionsQuery.data?.sections ?? []}
          onChangeAnswer={handleUpdateCaregiverStaffingPreferences}
          onClickGoBack={props.onClickGoBack}
        />
      );
    })
    .exhaustive();
};

export default StaffingPreferencesActivity;

const mapStaffingPreferenceTypeToUpdateRequestType = (type: CaregiverStaffingPreferenceTypes) => {
  switch (type) {
    case "languages":
      return "languages";
    case "main_language":
      return "mainLanguage";
    case "public_transprotation_time_limit":
      return "publicTransprotationTimeLimit";
    case "smoking_caregiver":
      return "smokingCaregiver";
    case "transportation_type":
      return "transportationType";
    case "work_with_bedbound":
      return "workWithBedbound";
    case "work_with_halal":
      return "workWithHalal";
    case "work_with_pets":
      return "workWithPets";
    case "work_with_hoyer_lift":
      return "workWithHoyerLift";
    case "work_with_kids":
      return "workWithKids";
    case "work_with_kosher":
      return "workWithKosher";
    case "work_with_shabat":
      return "workWithShabat";
    case "work_with_smoking_patient":
      return "workWithSmokingPatient";
    case "work_with_walking_asistant":
      return "workWithWalkingAsistant";
    case "additional_info":
      return "additionalInfo";
    case "cases_length":
      return "casesLength";
    case "patients_types":
      return "patientsTypes";
    case "studying":
      return "studying";
    case "studying_times":
      return "studyingTimes";
    case "work_times":
      return "workTimes";
    default:
      assertNever(type, true);
  }
};
